import {
    type CustomItemFormStructure,
    FlexyForm,
    type FlexyInputFormStructure,
    type FormStructure,
    type FormObject,
    FlexySelectFormStructure,
} from '@europrocurement/flexy-form';
import { Box, Button } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { dateToISOYmd } from '@europrocurement/l2d-utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/sharp-regular-svg-icons';
import { type OptionType } from '@europrocurement/flexy-components';
import { type UtilisateursJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { type DashboardFiltersType } from '../../containers/FormalistDashboard/FormalistDashboard';

type FiltersDashboarProps = {
    setFilters: React.Dispatch<React.SetStateAction<DashboardFiltersType>>;
    isSubmitting: boolean;
    initialStartDate: string;
    initialFormalistIds: number[];
    formalists?: UtilisateursJsonldUserworkgroupRead[];
};
const FiltersDashboard: React.FunctionComponent<FiltersDashboarProps> = function ({
    setFilters,
    isSubmitting,
    initialStartDate,
    formalists,
    initialFormalistIds,
}) {
    // Form
    const formContext = useForm<FormObject>({
        mode: 'onTouched',
        defaultValues: {
            startedAt: new Date(initialStartDate),
        },
    });

    useEffect(() => {
        const defaultFormalist = formalists?.find((f) => f.id === initialFormalistIds[0]);
        if (defaultFormalist) {
            formContext.setValue('formalist', {
                value: defaultFormalist.id,
                label: `${defaultFormalist.nom} ${defaultFormalist.prenom}`,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formalists]);

    const handleFilterSubmit = () => {
        if (isSubmitting) {
            return;
        }
        const { startedAt, endedAt, formalist } = formContext.getValues();

        setFilters({
            startDate: startedAt ? dateToISOYmd(startedAt as Date) : initialStartDate,
            endDate: endedAt ? dateToISOYmd(endedAt as Date) : undefined,
            formalistIds: formalist
                ? [(formalist as OptionType).value as number]
                : initialFormalistIds,
        });
    };

    const formStructure: FormStructure[] = [
        ...(formalists && formalists.length > 0
            ? [
                  {
                      type: 'select',
                      name: 'formalist',
                      size: 'small',
                      options: formalists.map((formalist) => ({
                          value: formalist.id,
                          label: `${formalist.nom} ${formalist.prenom}`,
                      })),
                      getOptionLabel: ({ label }: { label: string }) => label,
                      getOptionValue: ({ value }: { value: string }) => value,
                  } as FlexySelectFormStructure,
              ]
            : []),
        {
            type: 'date',
            name: 'startedAt',
            rules: { required: 'Ce champ est requis' },
            size: 'small',
        } as FlexyInputFormStructure,
        {
            type: 'date',
            name: 'endedAt',
            size: 'small',
        } as FlexyInputFormStructure,
        {
            type: 'customItem',
            name: 'submitButton',
            xs: 2,
            lg: 1,
            size: 'small',
            renderField: () => (
                <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    type="button"
                    onClick={handleFilterSubmit}
                    sx={{ transition: 'all ease-in-out 300ms', minWidth: 0, px: 1.5 }}
                >
                    <FontAwesomeIcon icon={faFilter} />
                </Button>
            ),
        } as CustomItemFormStructure,
    ];

    const formProps = {
        formContext,
        formObject: {},
        onSubmit: () => {},
        gridProps: {
            sx: {
                paddingX: '8px',
                gap: '8px',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexDirection: 'row',
                flexWrap: 'nowrap',
            },
        },
        submitButton: {
            label: '',
            displayed: false,
        },
        isSubmitting,
    };

    return (
        <Box
            onClick={(e) => {
                // Prevent accordion toggle expand on filters click
                e.stopPropagation();
            }}
        >
            <FlexyForm
                formStructure={formStructure}
                {...formProps}
            />
        </Box>
    );
};

export default FiltersDashboard;
